import { AdaptiveTable } from '../../app/adaptive-table.js';
import { SegmentedControl } from '@hue/design-system';
import {
  Button,
  CurrencyInput,
  DatePicker,
  DateRangePicker,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  Textarea,
  Tooltip,
  formatCivilRange,
  formatCurrency,
  readsAsPlural,
  type CivilDate,
  type StatusTone,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownToLine,
  Banknote,
  HandCoins,
  Info,
  Landmark,
  PencilLine,
  Percent,
  PiggyBank,
  Plus,
  Scale,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  contributionsApi,
  getApiError,
  partnersApi,
  type Contribution,
  type ContributionInput,
  type ContributionSort,
  type ContributionStatus,
  type ContributionStatusFilter,
  type ContributionType,
  type ContributionTypeFilter,
  type DepositStatus,
  type Partner,
  type RepaymentMode,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import { splitEvenly, type ShareState } from './share-split.js';
import {
  EmptyCard,
  ErrorCard,
  FilterGroup,
  FilterBar,
  LoadingCard,
  RequestAlert,
  SortableColumn,
  SummaryStrip,
} from '../../app/data-view.js';
import type { ContributionsView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

export interface ContributionCapabilities {
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canManageRepayments: boolean;
}

function InfoHint({ label, children }: { label: string; children: string }) {
  return (
    <Tooltip content={children}>
      <button
        type="button"
        aria-label={label}
        className="inline-grid size-6 shrink-0 cursor-pointer place-items-center rounded-full text-content-faint transition hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        <Info className="size-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

const typeLabels: Record<ContributionType, string> = {
  LOAN: 'Empréstimo',
  OWN_CASH: 'Caixa próprio',
  COMPANY_FUNDS: 'Recurso da empresa',
};

const repaymentLabels: Record<RepaymentMode, string> = {
  NONE: 'Não será reembolsado',
  FULL: 'Reembolso integral',
  INSTALLMENTS: 'Reembolso parcelado',
};

const statusLabels: Record<ContributionStatus, string> = {
  NOT_REPAYABLE: 'Não reembolsável',
  PENDING: 'A reembolsar',
  PARTIAL: 'Parcialmente reembolsado',
  REPAID: 'Reembolsado',
};

/// A leitura da situação, não a cor: quem pinta é a pílula do sistema, para o mesmo "resolvido"
/// sair igual aqui, nos lançamentos e em pessoas e acessos.
const statusTones: Record<ContributionStatus, StatusTone> = {
  NOT_REPAYABLE: 'neutral',
  PENDING: 'warning',
  PARTIAL: 'progress',
  REPAID: 'positive',
};

/// A situação da entrada do dinheiro, que não é a da devolução: um aporte pode estar inteiro
/// recebido e ainda ter tudo a reembolsar.
const depositLabels: Record<DepositStatus, string> = {
  NONE: 'Aplicado direto',
  PENDING: 'A receber',
  PARTIAL: 'Recebido parcialmente',
  RECEIVED: 'Recebido',
};

const depositTones: Record<DepositStatus, StatusTone> = {
  NONE: 'neutral',
  PENDING: 'warning',
  PARTIAL: 'progress',
  RECEIVED: 'positive',
};

/// Como o dinheiro chegou à empresa. O valor depositado é digitado, nunca deduzido das compras: o
/// vínculo entre compra e aporte diz "paga com esse dinheiro", não "paga por fora".
type ArrivalMode = 'DEPOSITED' | 'APPLIED' | 'PARTIAL';

const arrivalLabels: Record<ArrivalMode, string> = {
  DEPOSITED: 'Depositado na conta',
  APPLIED: 'Aplicado direto em compras',
  PARTIAL: 'Parte de cada',
};

const typeFilters: Array<{ value: ContributionTypeFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'LOAN', label: 'Empréstimo' },
  { value: 'OWN_CASH', label: 'Caixa próprio' },
  { value: 'COMPANY_FUNDS', label: 'Da empresa' },
];

const statusFilters: Array<{ value: ContributionStatusFilter; label: string }> = [
  { value: 'ALL', label: 'Todas' },
  { value: 'PENDING', label: 'Reembolsar' },
  { value: 'PARTIAL', label: 'Parciais' },
  { value: 'REPAID', label: 'Reembolsados' },
];

const columns: Array<{ sort: ContributionSort; label: string }> = [
  { sort: 'contributedOn', label: 'Data do aporte' },
  { sort: 'partner', label: 'Aporte' },
  { sort: 'amount', label: 'Valor líquido' },
  { sort: 'outstanding', label: 'Falta reembolsar' },
];

interface FormState {
  type: ContributionType;
  shares: ShareState[];
  description: string;
  amountCents: number | null;
  contributedOn: CivilDate | null;
  arrival: ArrivalMode;
  depositedAmountCents: number | null;
  depositorName: string;
  repaymentMode: RepaymentMode;
  expectedRepaymentOn: CivilDate | null;
  lender: string;
  installmentCount: string;
  installmentAmountCents: number | null;
  firstInstallmentOn: CivilDate | null;
  notes: string;
}

function emptyForm(today: CivilDate): FormState {
  return {
    type: 'OWN_CASH',
    shares: [{ partnerId: '', amountCents: null }],
    description: '',
    amountCents: null,
    contributedOn: today,
    arrival: 'DEPOSITED',
    depositedAmountCents: null,
    depositorName: '',
    repaymentMode: 'FULL',
    expectedRepaymentOn: null,
    lender: '',
    installmentCount: '',
    installmentAmountCents: null,
    firstInstallmentOn: null,
    notes: '',
  };
}

function fromContribution(contribution: Contribution): FormState {
  return {
    type: contribution.type,
    shares: contribution.shares.map((share) => ({
      partnerId: share.partner.id,
      amountCents: share.amountCents,
    })),
    description: contribution.description,
    amountCents: contribution.amountCents,
    contributedOn: contribution.contributedOn,
    arrival: arrivalOf(contribution),
    depositedAmountCents: contribution.depositedCents > 0 ? contribution.depositedCents : null,
    depositorName: contribution.depositorName ?? '',
    repaymentMode: contribution.repaymentMode,
    expectedRepaymentOn: contribution.expectedRepaymentOn,
    lender: contribution.lender ?? '',
    installmentCount:
      contribution.installmentCount === null ? '' : String(contribution.installmentCount),
    installmentAmountCents: contribution.installmentAmountCents,
    firstInstallmentOn: contribution.firstInstallmentOn,
    notes: contribution.notes ?? '',
  };
}

/// O modo vem do próprio valor depositado: guardar um rótulo à parte criaria duas versões da mesma
/// resposta, prontas para divergir no dia em que uma delas fosse editada.
function arrivalOf(contribution: Contribution): ArrivalMode {
  if (contribution.depositedCents <= 0) return 'APPLIED';
  return contribution.depositedCents >= contribution.amountCents ? 'DEPOSITED' : 'PARTIAL';
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(3).replace('.', ',')}%`;
}

export function CompanyContributions({
  capabilities,
  consolidated = false,
  timeZone,
  today,
  view,
  onView,
}: {
  capabilities: ContributionCapabilities;
  /// Sem unidade escolhida numa empresa de várias: os aportes das unidades alcançadas, só leitura.
  consolidated?: boolean;
  timeZone: string;
  today: CivilDate;
  view: ContributionsView;
  onView: ViewSetter<ContributionsView>;
}) {
  const queryClient = useQueryClient();
  const { search, type, status, from, to, sort, direction } = view;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Contribution>();
  const [form, setForm] = useState<FormState>(() => emptyForm(today));
  const [formError, setFormError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const [repaymentTarget, setRepaymentTarget] = useState<Contribution>();
  const [depositTarget, setDepositTarget] = useState<Contribution>();
  const [deleteTarget, setDeleteTarget] = useState<Contribution>();

  const filters = {
    ...(search.trim() ? { search: search.trim() } : {}),
    type,
    status,
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    sort,
    direction,
  };
  const page = useQuery({
    queryKey: ['company', 'contributions', filters],
    queryFn: () => contributionsApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const partners = useQuery({
    queryKey: ['company', 'partners', 'active'],
    queryFn: () => partnersApi.list({ activity: 'ACTIVE', sort: 'displayName', direction: 'asc' }),
    retry: false,
  });

  const partnerOptions = useMemo(
    () => [
      { value: '', label: 'Escolha o sócio' },
      ...(partners.data ?? []).map((partner: Partner) => ({
        value: partner.id,
        label: partner.displayName,
      })),
    ],
    [partners.data],
  );

  /// O que está filtrando agora, para virar etiqueta removível na barra.
  const activeFilters = [
    ...(type === 'ALL'
      ? []
      : [
          {
            key: 'type',
            label: typeFilters.find((item) => item.value === type)!.label,
            onRemove: () => onView({ type: 'ALL' }),
          },
        ]),
    ...(status === 'ALL'
      ? []
      : [
          {
            key: 'status',
            label: statusFilters.find((item) => item.value === status)!.label,
            onRemove: () => onView({ status: 'ALL' }),
          },
        ]),
    ...((from ?? to)
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from, to }, 'Qualquer dia'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];

  function clearFilters() {
    onView({ type: 'ALL', status: 'ALL', from: null, to: null });
  }

  const allowsPartners = form.type !== 'COMPANY_FUNDS';
  const isLoan = form.type === 'LOAN';
  const sharesTotal = form.shares.reduce((total, share) => total + (share.amountCents ?? 0), 0);
  const filledShares = form.shares.filter((share) => share.partnerId !== '');

  function toggleSort(column: ContributionSort) {
    if (sort === column) onView({ direction: direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: column === 'partner' ? 'asc' : 'desc' });
  }

  function change<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function changeShare(index: number, patch: Partial<ShareState>) {
    setForm((current) => ({
      ...current,
      shares: current.shares.map((share, position) =>
        position === index ? { ...share, ...patch } : share,
      ),
    }));
  }

  function openCreate() {
    setEditing(undefined);
    setForm(emptyForm(today));
    setFormError(undefined);
    setFormOpen(true);
  }

  function openEdit(contribution: Contribution) {
    setEditing(contribution);
    setForm(fromContribution(contribution));
    setFormError(undefined);
    setFormOpen(true);
  }

  async function save() {
    setFormError(undefined);
    const description = form.description.trim();
    if (description === '') return setFormError('Descreva o aporte.');
    if (form.amountCents === null || form.amountCents <= 0) {
      return setFormError('Informe o valor líquido do aporte.');
    }
    if (form.contributedOn === null) return setFormError('Informe a data do aporte.');
    if (form.type === 'OWN_CASH' && filledShares.length === 0) {
      return setFormError('Escolha de qual sócio veio o caixa próprio.');
    }
    if (allowsPartners && filledShares.length > 0 && sharesTotal !== form.amountCents) {
      return setFormError('A soma do rateio entre os sócios deve ser igual ao valor líquido.');
    }
    const installmentCount = form.installmentCount.trim();
    if (form.repaymentMode === 'INSTALLMENTS' && installmentCount === '') {
      return setFormError('Informe o parcelamento para reembolsar o aporte parcela a parcela.');
    }

    // Recurso da própria empresa nunca é depositado: o dinheiro já era dela.
    const depositedAmountCents =
      form.type === 'COMPANY_FUNDS' || form.arrival === 'APPLIED'
        ? 0
        : form.arrival === 'DEPOSITED'
          ? form.amountCents
          : (form.depositedAmountCents ?? 0);
    if (
      form.arrival === 'PARTIAL' &&
      form.type !== 'COMPANY_FUNDS' &&
      (depositedAmountCents <= 0 || depositedAmountCents >= form.amountCents)
    ) {
      return setFormError('O valor depositado deve ficar entre zero e o valor do aporte.');
    }

    const payload: ContributionInput = {
      type: form.type,
      shares: allowsPartners
        ? filledShares.map((share) => ({
            partnerId: share.partnerId,
            amountCents: share.amountCents ?? 0,
          }))
        : [],
      description,
      amountCents: form.amountCents,
      contributedOn: form.contributedOn,
      depositedAmountCents,
      depositorName: depositedAmountCents > 0 ? form.depositorName.trim() || null : null,
      repaymentMode: form.repaymentMode,
      expectedRepaymentOn: form.repaymentMode === 'NONE' ? null : form.expectedRepaymentOn,
      lender: isLoan ? form.lender.trim() || null : null,
      installmentCount: installmentCount === '' ? null : Number(installmentCount),
      installmentAmountCents: form.installmentAmountCents,
      firstInstallmentOn: form.firstInstallmentOn,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editing) await contributionsApi.update(editing.id, payload);
      else await contributionsApi.create(payload);
      await queryClient.invalidateQueries({ queryKey: ['company', 'contributions'] });
      setFormOpen(false);
    } catch (error) {
      setFormError(getApiError(error));
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  const totals = page.data?.totals;
  const contributions = page.data?.contributions ?? [];

  return (
    <section aria-labelledby="company-contributions-title">
      <PageHeader
        trail="Investimento inicial / Aportes"
        id="company-contributions-title"
        title="Aportes"
        aside={
          <InfoHint label="O que são aportes">
            Aporte é a origem do dinheiro usado na implantação. Registre cada empréstimo como um
            aporte separado; compras e despesas são lançadas em outra tela e podem apontar para essa
            origem.
          </InfoHint>
        }
        description="De onde veio o dinheiro que entrou na empresa, de quem é e quanto falta reembolsar."
        actions={
          <>
            {capabilities.canCreate ? (
              <CreateButton onClick={openCreate}>Registrar aporte</CreateButton>
            ) : null}
          </>
        }
      />

      {totals ? (
        <SummaryStrip
          label="Resumo dos aportes"
          items={[
            { label: 'Aportado', value: formatCurrency(totals.contributedCents) },
            {
              label: 'A receber',
              value: formatCurrency(totals.depositOutstandingCents),
              tone: 'warning',
            },
            { label: 'Reembolsado', value: formatCurrency(totals.repaidCents), tone: 'positive' },
            { label: 'Falta', value: formatCurrency(totals.outstandingCents), tone: 'warning' },
            { label: 'Juro', value: formatCurrency(totals.interestCents), tone: 'warning' },
          ]}
        />
      ) : null}

      {consolidated ? (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-surface-border bg-surface-card p-4 text-sm text-content-secondary"
        >
          Visão consolidada: aportes de todas as suas unidades, só para consulta. Para registrar ou
          alterar, escolha uma unidade no seletor acima.
        </p>
      ) : null}

      <FilterBar
        searchId="contribution-search"
        searchLabel="Buscar aporte"
        searchPlaceholder="Descrição, credor ou sócio"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={clearFilters}
        count={`${String(contributions.length)} ${contributions.length === 1 ? 'aporte' : 'aportes'}`}
        resultsLabel={`Ver ${String(contributions.length)} ${contributions.length === 1 ? 'aporte' : 'aportes'}`}
      >
        <FilterGroup
          label="Tipo"
          description="Filtrar aportes por tipo"
          options={typeFilters}
          value={type}
          onChange={(value) => onView({ type: value })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias</span>
          <DateRangePicker
            label="Período dos aportes"
            value={{ from, to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={today}
          />
        </div>
        <FilterGroup
          label="Situação"
          description="Filtrar aportes por situação"
          options={statusFilters}
          value={status}
          onChange={(value) => onView({ status: value })}
        />
      </FilterBar>

      {page.isLoading ? (
        <LoadingCard label="Carregando aportes" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : contributions.length ? (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <div className="overflow-x-auto">
            <AdaptiveTable>
              <table className="w-full border-collapse text-left">
                <caption className="sr-only">Aportes registrados na empresa</caption>
                <thead>
                  <tr className="border-b border-surface-border">
                    {columns.map((column) => (
                      <SortableColumn
                        key={column.sort}
                        column={column.sort}
                        label={column.label}
                        sort={sort}
                        direction={direction}
                        onToggle={toggleSort}
                      />
                    ))}
                    <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                      Situação
                    </th>
                    <th scope="col" className="px-5 py-3 text-xs font-semibold text-content/70">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {contributions.map((contribution) => (
                    <tr key={contribution.id}>
                      <td className="whitespace-nowrap px-5 py-4 text-xs text-content-muted">
                        {formatDate(`${contribution.contributedOn}T12:00:00Z`, timeZone)}
                      </td>
                      <td className="px-5 py-4">
                        <strong className="block text-sm font-medium">
                          {contribution.description}
                        </strong>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-content-faint">
                          <TypeIcon type={contribution.type} />
                          {typeLabels[contribution.type]}
                          {contribution.lender ? <span>· {contribution.lender}</span> : null}
                        </span>
                        <ShareList contribution={contribution} />
                        <InstallmentLine contribution={contribution} timeZone={timeZone} />
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm tabular-nums">
                        {formatCurrency(contribution.amountCents)}
                        {contribution.interest ? (
                          <span className="mt-1 flex items-center gap-1 text-xs text-content-faint">
                            <Percent className="size-3" aria-hidden="true" />
                            {formatRate(contribution.interest.monthlyRate)} a.m.
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm tabular-nums text-content-secondary">
                        {contribution.repaymentMode === 'NONE'
                          ? '—'
                          : formatCurrency(contribution.outstandingCents)}
                        {contribution.repaidCents > 0 ? (
                          <span className="mt-1 block text-xs text-content-faint">
                            {formatCurrency(contribution.repaidCents)} reembolsado
                          </span>
                        ) : null}
                      </td>
                      <td className="px-5 py-4">
                        <StatusPill tone={statusTones[contribution.status]}>
                          {statusLabels[contribution.status]}
                        </StatusPill>
                        {/* A entrada do dinheiro tem situação própria: o aporte pode estar inteiro
                          recebido e ainda ter tudo a reembolsar. */}
                        {contribution.depositedCents > 0 ? (
                          <StatusPill
                            tone={depositTones[contribution.depositStatus]}
                            className="mt-1.5"
                          >
                            {depositLabels[contribution.depositStatus]}
                            {contribution.depositStatus === 'RECEIVED' ? null : (
                              <span className="tabular-nums opacity-80">
                                {formatCurrency(contribution.depositOutstandingCents)}
                              </span>
                            )}
                          </StatusPill>
                        ) : null}
                        {contribution.expectedRepaymentOn ? (
                          <span className="mt-1 block text-xs text-content-faint">
                            Previsto para{' '}
                            {formatDate(`${contribution.expectedRepaymentOn}T12:00:00Z`, timeZone)}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-5 py-4">
                        {/* Só ícones, como na tela de lançamentos: a lista é longa e o dado é o que
                          a pessoa veio ler. */}
                        <div className="flex justify-end gap-1">
                          {capabilities.canManageRepayments && contribution.depositedCents > 0 ? (
                            <IconButton
                              label="Entrada do dinheiro"
                              icon={<ArrowDownToLine className="size-4" />}
                              onClick={() => setDepositTarget(contribution)}
                            />
                          ) : null}
                          {capabilities.canManageRepayments &&
                          contribution.repaymentMode !== 'NONE' ? (
                            <IconButton
                              label="Reembolsos"
                              icon={<Undo2 className="size-4" />}
                              onClick={() => setRepaymentTarget(contribution)}
                            />
                          ) : null}
                          {capabilities.canUpdate ? (
                            <IconButton
                              label="Editar"
                              icon={<PencilLine className="size-4" />}
                              onClick={() => openEdit(contribution)}
                            />
                          ) : null}
                          {capabilities.canDelete ? (
                            <IconButton
                              label="Excluir"
                              tone="danger"
                              icon={<Trash2 className="size-4" />}
                              onClick={() => setDeleteTarget(contribution)}
                            />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdaptiveTable>
          </div>
        </div>
      ) : (
        <EmptyCard
          icon={PiggyBank}
          title="Nenhum aporte encontrado"
          description="Ajuste os filtros ou registre de onde veio o dinheiro da empresa."
        />
      )}

      <Modal
        open={formOpen}
        onOpenChange={setFormOpen}
        title={editing ? 'Editar aporte' : 'Registrar aporte'}
        description="Declare o que é o dinheiro, de quem ele é e se precisa voltar."
      >
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <div className="grid gap-5">
            <div>
              <div className="mb-2 flex items-center gap-1">
                <span className="text-xs font-semibold text-content/70">Entenda o tipo</span>
                <InfoHint label="Como escolher o tipo do aporte">
                  Empréstimo é dinheiro recebido de um credor; caixa próprio saiu do bolso dos
                  sócios; recurso da empresa já pertencia à própria empresa. Você pode cadastrar
                  quantos empréstimos forem necessários.
                </InfoHint>
              </div>
              <Select
                label="Tipo do aporte"
                value={form.type}
                options={(Object.keys(typeLabels) as ContributionType[]).map((value) => ({
                  value,
                  label: typeLabels[value],
                }))}
                onValueChange={(value) => change('type', value as ContributionType)}
              />
            </div>
            <Input
              label="Descrição"
              autoComplete="off"
              value={form.description}
              onChange={(event) => change('description', event.target.value)}
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <CurrencyInput
                label="Valor líquido"
                hint="O compromisso de recurso, já sem o que o credor reteve."
                value={form.amountCents}
                onValueChange={(cents) => change('amountCents', cents)}
              />
              <DatePicker
                label="Data do aporte"
                hint="O dia em que o aporte foi reconhecido, não o dia em que o dinheiro caiu."
                value={form.contributedOn}
                onValueChange={(value) => change('contributedOn', value)}
                today={today}
              />
            </div>

            {form.type === 'COMPANY_FUNDS' ? null : (
              <fieldset className="rounded-2xl border border-surface-border bg-surface-elevated p-4">
                <legend className="px-2 text-xs font-semibold text-content/70">
                  <span className="inline-flex items-center gap-1">
                    Como o dinheiro chegou à empresa
                    <InfoHint label="Como o dinheiro do aporte chega à empresa">
                      Depositado é o que cai na conta da empresa e aparece no extrato: vira um
                      compromisso a receber, quitado pela conciliação ou por uma entrada registrada
                      à mão. Aplicado direto é o que já virou compra, sem passar pela conta.
                    </InfoHint>
                  </span>
                </legend>
                <SegmentedControl
                  label="Como o dinheiro do aporte chegou à empresa"
                  value={form.arrival}
                  onChange={(value) => change('arrival', value)}
                  options={(Object.keys(arrivalLabels) as ArrivalMode[]).map((value) => ({
                    value,
                    label: arrivalLabels[value],
                  }))}
                />
                {form.arrival === 'APPLIED' ? (
                  <p className="mt-3 text-xs text-content-muted">
                    Nada a receber: as compras pagas por quem aportou são a prova do dinheiro.
                  </p>
                ) : (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    {form.arrival === 'PARTIAL' ? (
                      <CurrencyInput
                        label="Valor depositado"
                        hint="Quanto caiu na conta, menor que o valor do aporte."
                        value={form.depositedAmountCents}
                        onValueChange={(cents) => change('depositedAmountCents', cents)}
                      />
                    ) : null}
                    <Input
                      label="Depositado por"
                      autoComplete="off"
                      hint="Opcional. Quem aparece no extrato — é o que a conciliação usa para propor o par."
                      value={form.depositorName}
                      onChange={(event) => change('depositorName', event.target.value)}
                    />
                  </div>
                )}
              </fieldset>
            )}

            {allowsPartners ? (
              <fieldset className="rounded-2xl border border-surface-border bg-surface-elevated p-4">
                <legend className="px-2 text-xs font-semibold text-content/70">
                  <span className="inline-flex items-center gap-1">
                    Sócios e rateio
                    <InfoHint label="Como funciona o rateio entre sócios">
                      Informe quanto do valor líquido pertence a cada sócio. Valores diferentes são
                      permitidos e juros do empréstimo seguem a mesma proporção do principal.
                    </InfoHint>
                  </span>
                </legend>
                <p className="mb-3 text-xs text-content-muted">
                  {form.type === 'LOAN'
                    ? 'Deixe vazio se o empréstimo está no CNPJ da empresa.'
                    : 'Quanto do valor líquido é de cada sócio.'}
                </p>
                <ul className="space-y-3">
                  {form.shares.map((share, index) => (
                    <li
                      key={index}
                      className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem_auto] sm:items-end"
                    >
                      <Select
                        label={`Sócio ${index + 1}`}
                        value={share.partnerId}
                        options={partnerOptions}
                        onValueChange={(value) => changeShare(index, { partnerId: value })}
                      />
                      <CurrencyInput
                        label="Valor do sócio"
                        value={share.amountCents}
                        onValueChange={(cents) => changeShare(index, { amountCents: cents })}
                      />
                      <IconButton
                        label={`Remover sócio ${index + 1}`}
                        icon={<Trash2 />}
                        tone="danger"
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            shares: current.shares.filter((_item, position) => position !== index),
                          }))
                        }
                      />
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() =>
                        setForm((current) => ({
                          ...current,
                          shares: [...current.shares, { partnerId: '', amountCents: null }],
                        }))
                      }
                    >
                      <Plus className="size-4" />
                      Adicionar sócio
                    </Button>
                    {/* O caso comum é meio a meio; digitar os dois valores à mão para chegar nisso
                        é trabalho que a tela pode fazer. O resultado continua editável, porque
                        divisão desigual também acontece. */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full sm:w-auto"
                      disabled={form.amountCents === null || filledShares.length < 2}
                      onClick={() =>
                        setForm((current) => ({ ...current, shares: splitEvenly(current) }))
                      }
                    >
                      <Scale className="size-4" />
                      Dividir igualmente
                    </Button>
                  </div>
                  {filledShares.length > 0 ? (
                    <p
                      className={`text-xs tabular-nums ${
                        form.amountCents !== null && sharesTotal !== form.amountCents
                          ? 'text-warning'
                          : 'text-content-muted'
                      }`}
                    >
                      Rateio: {formatCurrency(sharesTotal)}
                      {form.amountCents !== null && sharesTotal !== form.amountCents
                        ? ` de ${formatCurrency(form.amountCents)}`
                        : ''}
                    </p>
                  ) : null}
                </div>
              </fieldset>
            ) : null}

            <fieldset className="rounded-2xl border border-surface-border bg-surface-elevated p-4">
              <legend className="px-2 text-xs font-semibold text-content/70">
                <span className="inline-flex items-center gap-1">
                  Reembolso
                  <InfoHint label="Como funciona o reembolso do aporte">
                    Escolha se este aporte volta ou não. No parcelado, o saldo inclui todas as
                    parcelas — principal, juros e tarifas embutidos — e não apenas o valor que
                    entrou na empresa.
                  </InfoHint>
                </span>
              </legend>
              <SegmentedControl
                label="Como o aporte deve ser reembolsado"
                value={form.repaymentMode}
                onChange={(value) => change('repaymentMode', value)}
                options={(Object.keys(repaymentLabels) as RepaymentMode[]).map((value) => ({
                  value,
                  label: repaymentLabels[value],
                }))}
              />
              {form.repaymentMode === 'INSTALLMENTS' ? (
                <p className="mt-3 text-xs text-content-muted">
                  A empresa devolve parcela a parcela, acompanhando o parcelamento abaixo.
                </p>
              ) : null}
              {form.repaymentMode === 'NONE' ? null : (
                <div className="mt-4">
                  <DatePicker
                    label="Previsão de reembolso"
                    value={form.expectedRepaymentOn}
                    onValueChange={(value) => change('expectedRepaymentOn', value)}
                    today={today}
                    hint="Opcional. Deixe vazio quando ainda não houver data combinada."
                  />
                </div>
              )}
            </fieldset>

            <fieldset className="rounded-2xl border border-surface-border bg-surface-elevated p-4">
              <legend className="px-2 text-xs font-semibold text-content/70">
                <span className="inline-flex items-center gap-1">
                  Parcelamento
                  <InfoHint label="O que informar no parcelamento">
                    Cadastre o cronograma real do contrato. Em empréstimos, valor líquido é o que
                    entrou; parcelas vezes valor da parcela é o total que será devolvido.
                  </InfoHint>
                </span>
              </legend>
              <p className="mb-3 text-xs text-content-muted">
                Como o valor é pago ao longo do tempo. Opcional — vale para o carnê do empréstimo e
                para a compra que o sócio fez no cartão.
              </p>
              <div className="grid gap-4">
                {isLoan ? (
                  <Input
                    label="Credor"
                    autoComplete="off"
                    value={form.lender}
                    onChange={(event) => change('lender', event.target.value)}
                  />
                ) : null}
                <div className="grid gap-4 sm:grid-cols-3">
                  <Input
                    label="Parcelas"
                    inputMode="numeric"
                    autoComplete="off"
                    value={form.installmentCount}
                    onChange={(event) =>
                      change('installmentCount', event.target.value.replace(/\D/gu, ''))
                    }
                  />
                  <CurrencyInput
                    label="Valor da parcela"
                    value={form.installmentAmountCents}
                    onValueChange={(cents) => change('installmentAmountCents', cents)}
                  />
                  <DatePicker
                    label="1ª parcela"
                    value={form.firstInstallmentOn}
                    onValueChange={(value) => change('firstInstallmentOn', value)}
                    today={today}
                  />
                </div>
              </div>
              <InterestPreview form={form} />
            </fieldset>

            <Textarea
              label="Observação"
              rows={2}
              value={form.notes}
              onChange={(event) => change('notes', event.target.value)}
            />
          </div>

          {formError ? <RequestAlert message={formError} /> : null}

          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
            <Button
              className="w-full sm:w-auto"
              type="button"
              variant="outline"
              onClick={() => setFormOpen(false)}
            >
              Cancelar
            </Button>
            <Button className="w-full sm:w-auto" loading={saving}>
              {editing ? 'Salvar aporte' : 'Registrar aporte'}
            </Button>
          </div>
        </form>
      </Modal>

      {repaymentTarget ? (
        <RepaymentsDialog
          initial={repaymentTarget}
          today={today}
          timeZone={timeZone}
          onClose={() => setRepaymentTarget(undefined)}
          onChanged={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'contributions'] });
          }}
        />
      ) : null}

      {depositTarget ? (
        <DepositsDialog
          initial={depositTarget}
          today={today}
          timeZone={timeZone}
          onClose={() => setDepositTarget(undefined)}
          onChanged={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'contributions'] });
          }}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteDialog
          contribution={deleteTarget}
          onClose={() => setDeleteTarget(undefined)}
          onDeleted={async () => {
            await queryClient.invalidateQueries({ queryKey: ['company', 'contributions'] });
            setDeleteTarget(undefined);
          }}
        />
      ) : null}
    </section>
  );
}

function TypeIcon({ type }: { type: ContributionType }) {
  const className = 'size-3.5 shrink-0 text-brand-400';
  if (type === 'LOAN') return <Landmark className={className} aria-hidden="true" />;
  if (type === 'OWN_CASH') return <HandCoins className={className} aria-hidden="true" />;
  return <Banknote className={className} aria-hidden="true" />;
}

function ShareList({ contribution }: { contribution: Contribution }) {
  if (contribution.shares.length === 0) {
    return <span className="mt-1 block text-xs text-content-faint">Da própria empresa</span>;
  }
  return (
    <ul className="mt-1.5 flex flex-wrap gap-1.5">
      {contribution.shares.map((share) => (
        <li key={share.partner.id}>
          <StatusPill tone={statusTones[share.status]}>
            <strong className="font-medium">{share.partner.displayName}</strong>
            <span className="tabular-nums">principal {formatCurrency(share.amountCents)}</span>
            {share.repaidCents + share.outstandingCents !== share.amountCents ? (
              <span className="tabular-nums opacity-80">
                (total {formatCurrency(share.repaidCents + share.outstandingCents)})
              </span>
            ) : null}
            {share.repaidCents > 0 && share.status !== 'REPAID' ? (
              <span className="tabular-nums opacity-80">
                ({readsAsPlural(share.outstandingCents) ? 'faltam' : 'falta'}{' '}
                {formatCurrency(share.outstandingCents)})
              </span>
            ) : null}
          </StatusPill>
        </li>
      ))}
    </ul>
  );
}

function InstallmentLine({
  contribution,
  timeZone,
}: {
  contribution: Contribution;
  timeZone: string;
}) {
  if (contribution.installmentCount === null) return null;
  const amount = contribution.installmentAmountCents;
  const first = contribution.firstInstallmentOn;
  return (
    <span className="mt-1 block text-xs text-content-faint">
      {contribution.installmentCount}x{amount ? ` de ${formatCurrency(amount)}` : ''}
      {first ? `, 1ª em ${formatDate(`${first}T12:00:00Z`, timeZone)}` : ''}
      {contribution.interest && contribution.interest.interestCents > 0
        ? ` · juro ${formatCurrency(contribution.interest.interestCents)}`
        : ''}
    </span>
  );
}

/// O custo do dinheiro aparece enquanto se digita o carnê: é o número que o negócio confere à mão.
function InterestPreview({ form }: { form: FormState }) {
  const count = Number(form.installmentCount);
  if (!count || form.installmentAmountCents === null || form.amountCents === null) return null;

  const totalPaid = count * form.installmentAmountCents;
  const interest = totalPaid - form.amountCents;
  if (interest <= 0) return null;
  return (
    <dl className="mt-4 grid gap-3 border-t border-surface-border pt-4 text-xs sm:grid-cols-3">
      <div>
        <dt className="text-content-faint">Total pago</dt>
        <dd className="mt-0.5 font-semibold tabular-nums">{formatCurrency(totalPaid)}</dd>
      </div>
      <div>
        <dt className="text-content-faint">Juro total</dt>
        <dd className="mt-0.5 font-semibold tabular-nums text-warning">
          {formatCurrency(interest)}
        </dd>
      </div>
      <div>
        <dt className="text-content-faint">Juro médio mensal</dt>
        <dd className="mt-0.5 font-semibold tabular-nums">
          {formatCurrency(Math.round(interest / count))}
        </dd>
      </div>
    </dl>
  );
}

/// O diálogo mantém a própria cópia do aporte e a atualiza com a resposta de cada reembolso, em vez
/// de reprocurar na lista: o saldo e a situação mudam na hora, sem esperar a lista recarregar.
function RepaymentsDialog({
  initial,
  today,
  timeZone,
  onClose,
  onChanged,
}: {
  initial: Contribution;
  today: CivilDate;
  timeZone: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [contribution, setContribution] = useState(initial);
  const [partnerId, setPartnerId] = useState(initial.shares[0]?.partner.id ?? '');
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [paidOn, setPaidOn] = useState<CivilDate | null>(today);
  const [installmentNumber, setInstallmentNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const hasShares = contribution.shares.length > 0;
  const byInstallments = contribution.repaymentMode === 'INSTALLMENTS';

  async function add() {
    setError(undefined);
    if (hasShares && partnerId === '') return setError('Escolha para qual sócio o reembolso foi.');
    if (amountCents === null || amountCents <= 0) return setError('Informe o valor reembolsado.');
    if (paidOn === null) return setError('Informe a data do reembolso.');
    setBusy(true);
    try {
      setContribution(
        await contributionsApi.addRepayment(contribution.id, {
          partnerId: hasShares ? partnerId : null,
          amountCents,
          paidOn,
          installmentNumber: installmentNumber === '' ? null : Number(installmentNumber),
          notes: notes.trim() || null,
        }),
      );
      await onChanged();
      setAmountCents(null);
      setInstallmentNumber('');
      setNotes('');
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
    return undefined;
  }

  async function remove(repaymentId: string) {
    setError(undefined);
    setBusy(true);
    try {
      setContribution(await contributionsApi.removeRepayment(contribution.id, repaymentId));
      await onChanged();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Reembolsos do aporte"
      description={`${contribution.description} · ${formatCurrency(contribution.amountCents)}`}
    >
      {hasShares ? (
        <ul className="mb-5 space-y-2">
          {contribution.shares.map((share) => (
            <li
              key={share.partner.id}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-xs"
            >
              <strong className="text-sm font-medium">{share.partner.displayName}</strong>
              <span className="tabular-nums text-content-muted">
                {formatCurrency(share.repaidCents)} de{' '}
                {formatCurrency(share.repaidCents + share.outstandingCents)}
              </span>
              <StatusPill tone={statusTones[share.status]} className="ml-auto">
                {statusLabels[share.status]}
              </StatusPill>
            </li>
          ))}
        </ul>
      ) : (
        <div className="mb-5 flex flex-wrap gap-4 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-xs">
          <span>
            Reembolsado:{' '}
            <strong className="tabular-nums text-positive">
              {formatCurrency(contribution.repaidCents)}
            </strong>
          </span>
          <span>
            Falta:{' '}
            <strong className="tabular-nums text-warning">
              {formatCurrency(contribution.outstandingCents)}
            </strong>
          </span>
          <span>
            Situação: <strong>{statusLabels[contribution.status]}</strong>
          </span>
        </div>
      )}

      {contribution.repayments.length ? (
        <ul className="mb-5 divide-y divide-surface-border rounded-xl border border-surface-border">
          {contribution.repayments.map((repayment) => (
            <li key={repayment.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="text-sm font-medium tabular-nums">
                {formatCurrency(repayment.amountCents)}
              </span>
              {repayment.partner ? (
                <span className="text-xs text-content-secondary">
                  {repayment.partner.displayName}
                </span>
              ) : null}
              <span className="text-xs text-content-muted">
                {formatDate(`${repayment.paidOn}T12:00:00Z`, timeZone)}
              </span>
              {repayment.installmentNumber ? (
                <span className="text-xs text-content-faint">
                  parcela {repayment.installmentNumber}
                </span>
              ) : null}
              {repayment.notes ? (
                <span className="text-xs text-content-faint">{repayment.notes}</span>
              ) : null}
              <IconButton
                label={`Remover reembolso de ${formatCurrency(repayment.amountCents)}`}
                icon={<Trash2 />}
                tone="danger"
                onClick={() => void remove(repayment.id)}
                disabled={busy}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-5 rounded-xl border border-dashed border-surface-border px-4 py-5 text-center text-xs text-content-muted">
          Nenhum reembolso registrado ainda.
        </p>
      )}

      {contribution.status === 'REPAID' ? null : (
        <div className="grid gap-4 border-t border-surface-border pt-5 sm:grid-cols-2">
          {hasShares ? (
            <Select
              label="Reembolsar para"
              value={partnerId}
              options={contribution.shares.map((share) => ({
                value: share.partner.id,
                label: share.partner.displayName,
              }))}
              onValueChange={setPartnerId}
            />
          ) : null}
          <CurrencyInput
            label="Valor reembolsado"
            value={amountCents}
            onValueChange={setAmountCents}
          />
          <DatePicker
            label="Data do reembolso"
            value={paidOn}
            onValueChange={setPaidOn}
            today={today}
          />
          {byInstallments ? (
            <Input
              label="Parcela reembolsada"
              inputMode="numeric"
              autoComplete="off"
              hint={
                contribution.installmentCount
                  ? `De 1 a ${contribution.installmentCount}.`
                  : undefined
              }
              value={installmentNumber}
              onChange={(event) => setInstallmentNumber(event.target.value.replace(/\D/gu, ''))}
            />
          ) : null}
          <Input
            className="sm:col-span-2"
            label="Observação"
            autoComplete="off"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      )}

      {error ? <RequestAlert message={error} /> : null}

      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Concluir
        </Button>
        {contribution.status === 'REPAID' ? null : (
          <Button
            className="w-full sm:w-auto"
            type="button"
            loading={busy}
            onClick={() => void add()}
          >
            Registrar reembolso
          </Button>
        )}
      </div>
    </Modal>
  );
}

/// As entradas do dinheiro na conta. Só a registrada à mão sai por aqui: a que veio da conciliação
/// pertence ao vínculo do extrato, e desfazê-la é desfazer a conciliação no Financeiro (ADR 0021).
function DepositsDialog({
  initial,
  today,
  timeZone,
  onClose,
  onChanged,
}: {
  initial: Contribution;
  today: CivilDate;
  timeZone: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [contribution, setContribution] = useState(initial);
  const [amountCents, setAmountCents] = useState<number | null>(null);
  const [receivedOn, setReceivedOn] = useState<CivilDate | null>(today);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const done = contribution.depositStatus === 'RECEIVED';

  async function add() {
    setError(undefined);
    if (amountCents === null || amountCents <= 0) return setError('Informe o valor recebido.');
    if (receivedOn === null) return setError('Informe a data em que o dinheiro entrou.');
    setBusy(true);
    try {
      setContribution(
        await contributionsApi.addDeposit(contribution.id, {
          amountCents,
          receivedOn,
          notes: notes.trim() || null,
        }),
      );
      await onChanged();
      setAmountCents(null);
      setNotes('');
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
    return undefined;
  }

  async function remove(depositId: string) {
    setError(undefined);
    setBusy(true);
    try {
      setContribution(await contributionsApi.removeDeposit(contribution.id, depositId));
      await onChanged();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Entrada do aporte"
      description={`${contribution.description} · ${formatCurrency(contribution.depositedCents)} combinados para a conta da empresa`}
      footer={
        <>
          <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
            Concluir
          </Button>
          {done ? null : (
            <Button
              className="w-full sm:w-auto"
              type="button"
              loading={busy}
              onClick={() => void add()}
            >
              Registrar entrada
            </Button>
          )}
        </>
      }
    >
      <div className="mb-5 flex flex-wrap gap-4 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-xs">
        <span>
          Recebido:{' '}
          <strong className="tabular-nums text-positive">
            {formatCurrency(contribution.receivedCents)}
          </strong>
        </span>
        <span>
          Falta:{' '}
          <strong className="tabular-nums text-warning">
            {formatCurrency(contribution.depositOutstandingCents)}
          </strong>
        </span>
        <StatusPill tone={depositTones[contribution.depositStatus]} className="ml-auto">
          {depositLabels[contribution.depositStatus]}
        </StatusPill>
      </div>

      {contribution.deposits.length ? (
        <ul className="mb-5 divide-y divide-surface-border rounded-xl border border-surface-border">
          {contribution.deposits.map((deposit) => (
            <li key={deposit.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="text-sm font-medium tabular-nums">
                {formatCurrency(deposit.amountCents)}
              </span>
              <span className="text-xs text-content-muted">
                {formatDate(`${deposit.receivedOn}T12:00:00Z`, timeZone)}
              </span>
              <span className="text-xs text-content-faint">
                {deposit.origin === 'RECONCILIATION' ? 'pela conciliação' : 'recebido por fora'}
              </span>
              {deposit.notes ? (
                <span className="text-xs text-content-faint">{deposit.notes}</span>
              ) : null}
              {deposit.origin === 'RECONCILIATION' ? (
                <span className="ml-auto text-xs text-content-faint">
                  Desfaça no Financeiro, pelo vínculo
                </span>
              ) : (
                <IconButton
                  label={`Remover entrada de ${formatCurrency(deposit.amountCents)}`}
                  icon={<Trash2 />}
                  tone="danger"
                  onClick={() => void remove(deposit.id)}
                  disabled={busy}
                />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-5 rounded-xl border border-dashed border-surface-border px-4 py-5 text-center text-xs text-content-muted">
          Nenhuma entrada registrada ainda. O depósito que passou pela conta da empresa chega pela
          conciliação do extrato.
        </p>
      )}

      {done ? null : (
        <div className="grid gap-4 border-t border-surface-border pt-5 sm:grid-cols-2">
          <CurrencyInput
            label="Valor recebido"
            value={amountCents}
            onValueChange={setAmountCents}
          />
          <DatePicker
            label="Data da entrada"
            value={receivedOn}
            onValueChange={setReceivedOn}
            today={today}
          />
          <Input
            className="sm:col-span-2"
            label="Observação"
            autoComplete="off"
            hint="Use para dizer por onde o dinheiro entrou, já que não foi pela conta cadastrada."
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      )}

      {error ? <RequestAlert message={error} /> : null}
    </Modal>
  );
}

function DeleteDialog({
  contribution,
  onClose,
  onDeleted,
}: {
  contribution: Contribution;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setError(undefined);
    setBusy(true);
    try {
      await contributionsApi.remove(contribution.id);
      await onDeleted();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onClose}
      title="Excluir aporte"
      description="O aporte sai da conciliação junto com o rateio e os reembolsos registrados nele."
    >
      <p className="text-sm leading-6 text-content-secondary">
        Excluir <strong>{contribution.description}</strong>, de{' '}
        {formatCurrency(contribution.amountCents)}?
        {contribution.repayments.length
          ? ` ${contribution.repayments.length} reembolso(s) registrado(s) também serão apagados.`
          : ''}
      </p>
      {error ? <RequestAlert message={error} /> : null}
      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button className="w-full sm:w-auto" type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          className="w-full sm:w-auto"
          type="button"
          loading={busy}
          onClick={() => void confirm()}
        >
          Excluir aporte
        </Button>
      </div>
    </Modal>
  );
}
